import { useState, useEffect, type ReactNode } from 'react'
import { isCypressWindow } from '@retailer-platform/shared-common'

declare global {
  interface Window {
    __MOCKS_READY__?: boolean
  }
}
/**
 * A gate component that prevents rendering children until mocks are ready.
 *
 * Automatically detects Cypress environment and waits for mocks:
 * 1. In Cypress: waits for `window.__MOCKS_READY__` to be true before rendering
 * 2. In production: renders children immediately
 *
 * Usage in Cypress tests:
 * - Apply your mocks in `onLoad`
 * - Call `DashboardCoreMocking.signalMocksReady(win)` after all mocks are applied
 */
export const MockGate = ({ children }: { children: ReactNode }) => {
  // Auto-detect Cypress environment - no manual flag needed
  const isInCypress = isCypressWindow()
  const [ready, setReady] = useState(!isInCypress)

  useEffect(() => {
    if (!isInCypress) return

    console.debug('[MockGate] Cypress detected, waiting for mocks to be ready...')

    // Poll for mocks ready signal
    const interval = setInterval(() => {
      if (window.__MOCKS_READY__) {
        console.debug('[MockGate] Mocks are ready, proceeding with render')
        setReady(true)
        clearInterval(interval)
      }
    }, 10)

    return () => clearInterval(interval)
  }, [isInCypress])

  if (!ready) {
    console.debug('[MockGate] Blocking render until mocks are ready')
    return null
  }

  return (
    <>
      {isInCypress && <div data-testid="mockgate-ready" style={{ display: 'none' }} />}
      {children}
    </>
  )
}
